import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaPhone,
  FaGift,
  FaHeart,
  FaCog,
  FaCheckCircle,
  FaCalendarAlt,
  FaEdit,
  FaDollarSign,
  FaTicketAlt,
  FaMapMarkerAlt,
  FaCalendarCheck,
  FaHistory,
  FaQrcode,
  FaEyeSlash,
  FaCreditCard,
  FaPaypal,
  FaTrash,
  FaExchangeAlt,
  FaTrophy,
  FaGavel,
} from "react-icons/fa";
import "./Profile.css";
import LoadingPage from "../../components/Loading/LoadingPage";
import { useAuth } from "../../contexts/AuthContext";
import { appendCompanyCode } from "../../hooks/useCompanyCode";
import { getMyTickets } from "../../services/ticketingService";
import {
  getCustomerPaymentMethod,
  removeCustomerPaymentMethod,
  setupCustomerCard,
  confirmCustomerCard,
  setupCustomerPaypal,
  confirmCustomerPaypal,
  // setupCustomerVenmo,
  // confirmCustomerVenmo,
  getAuctionWins,
  submitWinnerFulfillment,
} from "../../services/auctionService";
// import VenmoLinkButton from "../../components/Ticketing/VenmoLinkButton";
import blockClubLogo from "../../media/BC.png";

// Helper function to extract percent from title (e.g., "20% off" -> 20)
function extractPercent(title) {
  if (!title) return null;
  const match = title.match(/(\d+)%/);
  return match ? parseInt(match[1], 10) : null;
}

// Helper function to parse price_text and return range object (e.g., "$10–20" -> {low: 10, high: 20})
function parsePriceLevel(priceText) {
  if (!priceText) return { low: 20, high: 30 }; // Default to mid-range price

  // Check if it's a price range like "$10–20" or "$10-20"
  const rangeMatch = priceText.match(/\$(\d+)[–-]\$?(\d+)/);
  if (rangeMatch) {
    const low = parseInt(rangeMatch[1], 10);
    const high = parseInt(rangeMatch[2], 10);
    return { low, high };
  }

  // Check if it's a single price like "$15"
  const singlePriceMatch = priceText.match(/\$(\d+)/);
  if (singlePriceMatch) {
    const price = parseInt(singlePriceMatch[1], 10);
    return { low: price, high: price };
  }

  // Fallback to dollar sign count (e.g., "$$$" -> 3)
  const count = (priceText.match(/\$/g) || []).length;
  if (count > 0) {
    // Convert count to approximate price: $ = 15, $$ = 25, $$$ = 40, $$$$ = 60
    const priceMap = { 1: 15, 2: 25, 3: 40, 4: 60 };
    const price = priceMap[count] || 25;
    return { low: price, high: price };
  }

  return { low: 20, high: 30 }; // Default fallback
}

// Calculate estimated dollar savings based on percent and price range
function calculateDollarSavings(percent, priceRange) {
  if (!percent || !priceRange) return 0;
  const avgPrice = (priceRange.low + priceRange.high) / 2;
  return Math.round((avgPrice * percent) / 100);
}

// Helper function to format tag name for display (remove underscores and special characters)
function formatTagName(tagName) {
  if (!tagName) return '';
  return tagName
    .replace(/_/g, ' ')  // Replace underscores with spaces
    .replace(/[^\w\s]/g, '')  // Remove special characters except letters, numbers, and spaces
    .trim();  // Remove leading/trailing whitespace
}

const Profile = () => {
  const navigate = useNavigate();
  const { customerToken, viewMode, setShowLoading } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

  const [loading, setLoading] = useState(true);
  const [customerData, setCustomerData] = useState(null);
  const [redeemedCoupons, setRedeemedCoupons] = useState([]);
  const [preferences, setPreferences] = useState([]);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("tickets"); // tickets, payment, redeemed
  const [venueDetails, setVenueDetails] = useState({}); // Store venue details by venue_id
  const [totalSavings, setTotalSavings] = useState(0);
  const [myTickets, setMyTickets] = useState([]);
  const [expandedTicketEvents, setExpandedTicketEvents] = useState(new Set());

  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [linkingCard, setLinkingCard] = useState(false);
  const [linkingPaypal, setLinkingPaypal] = useState(false);
  const [removingPayment, setRemovingPayment] = useState(false);
  const [paymentReturnHandled, setPaymentReturnHandled] = useState(false);
  const [auctionWins, setAuctionWins] = useState([]);
  const [winsError, setWinsError] = useState(null);

  // Fulfillment editing state (pickup/delivery change)
  const [fulfillmentEditId, setFulfillmentEditId] = useState(null);
  const [fulfillmentPref, setFulfillmentPref] = useState("pickup");
  const [fulfillmentAddress, setFulfillmentAddress] = useState("");
  const [fulfillmentTime, setFulfillmentTime] = useState("");
  const [fulfillmentSaving, setFulfillmentSaving] = useState(false);
  const [fulfillmentErr, setFulfillmentErr] = useState("");

  const toggleTicketExpand = (eventId) => {
    setExpandedTicketEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const couponsPerPage = 9;

  // Refs for tab buttons, tabs container, and content section
  const redeemedTabRef = useRef(null);
  const ticketsTabRef = useRef(null);
  const preferencesTabRef = useRef(null);
  const paymentTabRef = useRef(null);
  const winsTabRef = useRef(null);
  const tabsContainerRef = useRef(null);
  const contentRef = useRef(null);

  // Redirect if not logged in
  useEffect(() => {
    if (viewMode === "public" || !customerToken || customerToken === "public") {
      navigate(appendCompanyCode("/perks-directory"));
    }
  }, [viewMode, customerToken, navigate]);

  // Control header/footer visibility during loading
  useEffect(() => {
    setShowLoading(loading);
  }, [loading, setShowLoading]);

  // Fetch customer data, redeemed coupons, and preferences
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!customerToken || customerToken === "public") return;

      try {
        setLoading(true);
        setError(null);

        // Fetch customer coupons (includes redeemed ones)
        const couponsRes = await fetch(
          `${apiUrl}/coupon/customer/${encodeURIComponent(customerToken)}`
        );

        if (!couponsRes.ok) {
          throw new Error("Failed to fetch customer data");
        }

        const couponsData = await couponsRes.json();
        setCustomerData(couponsData);

        // Filter redeemed coupons - venue info is now included in the coupon data
        const redeemed = (couponsData.coupons || []).filter(
          (c) => c.status === "redeemed"
        );
        setRedeemedCoupons(redeemed);

        // Fetch venue details for each redeemed coupon to calculate savings
        const uniqueVenueIds = [...new Set(redeemed.map(c => c.venue_id).filter(Boolean))];
        const venueDetailsMap = {};

        await Promise.all(
          uniqueVenueIds.map(async (venueId) => {
            try {
              const venueRes = await fetch(
                `${apiUrl}/coupon/venue/${venueId}/details`
              );
              if (venueRes.ok) {
                const venueData = await venueRes.json();
                venueDetailsMap[venueId] = venueData;
              }
            } catch (err) {
              console.warn(`Could not fetch details for venue ${venueId}:`, err);
            }
          })
        );

        setVenueDetails(venueDetailsMap);

        // Calculate total savings
        let savings = 0;
        redeemed.forEach((coupon) => {
          const percent = extractPercent(coupon.title);
          if (percent && venueDetailsMap[coupon.venue_id]) {
            const priceRange = parsePriceLevel(venueDetailsMap[coupon.venue_id].price_text);
            const couponSavings = calculateDollarSavings(percent, priceRange);
            savings += couponSavings;
          }
        });
        setTotalSavings(savings);

        // Fetch preferences, tickets and auction wins in parallel
        const [ticketsData, winsData] = await Promise.all([
          getMyTickets(customerToken).catch(() => []),
          getAuctionWins(customerToken).catch((err) => { console.warn("Auction wins fetch failed:", err); setWinsError(err.message || "Failed to load wins"); return []; }),
          (async () => {
            try {
              const email = localStorage.getItem("loyalty_email");
              if (email) {
                const prefsRes = await fetch(
                  `${apiUrl}/loyalty/answers/${encodeURIComponent(email)}`
                );
                if (prefsRes.ok) {
                  const prefsData = await prefsRes.json();
                  setPreferences(prefsData.tags || []);
                }
              }
            } catch (err) {
              console.warn("Could not load preferences:", err);
            }
          })(),
        ]);

        setMyTickets(Array.isArray(ticketsData) ? ticketsData : []);
        setAuctionWins(Array.isArray(winsData) ? winsData : []);
      } catch (err) {
        console.error("Error fetching profile data:", err);
        setError(err.message || "Failed to load profile data");
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [customerToken, apiUrl]);

  // Fetch payment method
  useEffect(() => {
    if (!customerToken || customerToken === "public") return;
    setPaymentLoading(true);
    getCustomerPaymentMethod(customerToken)
      .then(setPaymentMethod)
      .catch(() => {})
      .finally(() => setPaymentLoading(false));
  }, [customerToken]);

  // Handle return from Stripe or PayPal payment setup
  useEffect(() => {
    if (!customerToken || customerToken === "public" || paymentReturnHandled) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const approvalTokenId = params.get("approval_token_id");
    const isStripeReturn = params.get("payment_setup") === "complete" && sessionId;
    const isPaypalReturn = params.get("paypal_vault") === "return" && approvalTokenId;

    if (!isStripeReturn && !isPaypalReturn) return;
    setPaymentReturnHandled(true);
    setActiveTab("payment");

    const cleanUrl = () => {
      const url = new URL(window.location.href);
      ["payment_setup", "session_id", "paypal_vault", "approval_token_id"].forEach((k) =>
        url.searchParams.delete(k)
      );
      window.history.replaceState({}, "", url.toString());
    };

    if (isStripeReturn) {
      confirmCustomerCard(customerToken, sessionId)
        .then((result) => {
          setPaymentMethod({ has_payment_method: true, type: "stripe", last4: result.last4, brand: result.brand });
          cleanUrl();
        })
        .catch(() => cleanUrl());
    } else {
      confirmCustomerPaypal(customerToken, approvalTokenId)
        .then((result) => {
          setPaymentMethod({ has_payment_method: true, type: "paypal", paypal_email: result.paypal_email });
          cleanUrl();
        })
        .catch(() => cleanUrl());
    }
  }, [customerToken, paymentReturnHandled]);

  const handleLinkCard = async () => {
    if (!customerToken) return;
    setPaymentError("");
    setLinkingCard(true);
    try {
      const base = window.location.href.split("?")[0];
      const { checkout_url } = await setupCustomerCard(customerToken, base, base);
      window.location.href = checkout_url;
    } catch (err) {
      setPaymentError(err.message || "Failed to start card setup. Please try again.");
      setLinkingCard(false);
    }
  };

  const handleLinkPaypal = async () => {
    if (!customerToken) return;
    setPaymentError("");
    setLinkingPaypal(true);
    try {
      const base = window.location.href.split("?")[0];
      const { approve_url } = await setupCustomerPaypal(customerToken, base, base);
      window.location.href = approve_url;
    } catch (err) {
      setPaymentError(err.message || "Failed to start PayPal setup. Please try again.");
      setLinkingPaypal(false);
    }
  };

  // const [showVenmoButton, setShowVenmoButton] = useState(false);

  // const handleVenmoSetupToken = async () => {
  //   const { token_id } = await setupCustomerVenmo(customerToken);
  //   return token_id;
  // };

  // const handleVenmoApprove = async (vaultSetupToken) => {
  //   try {
  //     const result = await confirmCustomerVenmo(customerToken, vaultSetupToken);
  //     setPaymentMethod({ has_payment_method: true, type: "venmo", venmo_handle: result.venmo_handle });
  //     setShowVenmoButton(false);
  //   } catch (err) {
  //     setPaymentError(err.message || "Venmo setup failed. Please try again.");
  //   }
  // };

  const handleRemovePayment = async () => {
    if (!window.confirm("Remove your saved payment method? You'll need to re-link it to bid in future auctions.")) return;
    setPaymentError("");
    setRemovingPayment(true);
    try {
      await removeCustomerPaymentMethod(customerToken);
      setPaymentMethod({ has_payment_method: false });
    } catch (err) {
      setPaymentError(err.message || "Failed to remove payment method.");
    } finally {
      setRemovingPayment(false);
    }
  };

  const handleEditPreferences = () => {
    // Get the customer email from localStorage or customerData
    const email = localStorage.getItem("loyalty_email");
    if (email) {
      // Go directly to questions, skip the summary page
      navigate(`/loyalty-questionnaire?email=${encodeURIComponent(email)}&edit=true&skip_summary=true`);
    }
  };

  const handleGoToVenueCoupons = (venueId) => {
    if (!venueId) return;
    navigate(`/coupons/${customerToken}/venue/${venueId}`);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);

    // Scroll the selected tab button into view horizontally
    let tabRef = null;
    if (tab === "redeemed") tabRef = redeemedTabRef;
    else if (tab === "tickets") tabRef = ticketsTabRef;
    else if (tab === "preferences") tabRef = preferencesTabRef;
    else if (tab === "payment") tabRef = paymentTabRef;
    else if (tab === "wins") tabRef = winsTabRef;

    if (tabRef && tabRef.current) {
      tabRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center"
      });
    }

    // Scroll so the tab bar sits at the top of the viewport
    if (tabsContainerRef.current) {
      tabsContainerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Pagination calculations
  const indexOfLastCoupon = currentPage * couponsPerPage;
  const indexOfFirstCoupon = indexOfLastCoupon - couponsPerPage;
  const currentCoupons = redeemedCoupons.slice(indexOfFirstCoupon, indexOfLastCoupon);
  const totalPages = Math.ceil(redeemedCoupons.length / couponsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openFulfillmentEdit = (win) => {
    setFulfillmentEditId(win.item_id);
    setFulfillmentPref(win.delivery_preference || "pickup");
    setFulfillmentAddress(win.delivery_address || "");
    setFulfillmentTime(win.preferred_time || "");
    setFulfillmentErr("");
  };

  const handleFulfillmentSave = async (win) => {
    if (fulfillmentPref === "delivery" && !fulfillmentAddress.trim()) {
      setFulfillmentErr("Please enter a delivery address.");
      return;
    }
    setFulfillmentSaving(true);
    setFulfillmentErr("");
    try {
      await submitWinnerFulfillment(win.event_id, win.item_id, {
        customer_token: customerToken,
        delivery_preference: fulfillmentPref,
        delivery_address: fulfillmentPref === "delivery" ? fulfillmentAddress.trim() : null,
        preferred_time: fulfillmentTime.trim() || null,
      });
      setAuctionWins((prev) =>
        prev.map((w) =>
          w.item_id === win.item_id
            ? { ...w, delivery_preference: fulfillmentPref, delivery_address: fulfillmentAddress.trim(), preferred_time: fulfillmentTime.trim() }
            : w
        )
      );
      setFulfillmentEditId(null);
    } catch (err) {
      setFulfillmentErr(err.message || "Failed to save. Please try again.");
    } finally {
      setFulfillmentSaving(false);
    }
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <div className="error-state">
            <p>{error}</p>
            <button className="btn-retry" onClick={() => window.location.reload()}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-container">
        {/* Hero */}
        <div className="profile-hero">
          <div className="profile-hero-top">
            <div className="profile-hero-avatar">
              <FaTicketAlt />
            </div>
            <div className="profile-hero-identity">
              <div className="profile-hero-name">Block Club Member</div>
              {customerData?.phone && (
                <div className="profile-hero-phone">
                  <FaPhone /> {customerData.phone}
                </div>
              )}
            </div>
          </div>
          <div className="profile-hero-stats">
            <div className="profile-hero-stat">
              <span className="profile-hero-stat-num">
                {new Set(myTickets.map((t) => t.event_id)).size}
              </span>
              <span className="profile-hero-stat-label">Events</span>
            </div>
            <div className="profile-hero-stat">
              <span className="profile-hero-stat-num">{auctionWins.length}</span>
              <span className="profile-hero-stat-label">Wins</span>
            </div>
            <div className="profile-hero-stat">
              <span className="profile-hero-stat-num">{redeemedCoupons.length}</span>
              <span className="profile-hero-stat-label">Perks</span>
            </div>
            {totalSavings > 0 && (
              <div className="profile-hero-stat">
                <span className="profile-hero-stat-num">~${totalSavings}</span>
                <span className="profile-hero-stat-label">Saved</span>
              </div>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="profile-tabs" ref={tabsContainerRef}>
          <button
            ref={ticketsTabRef}
            className={`profile-tab ${activeTab === "tickets" ? "active" : ""}`}
            onClick={() => handleTabChange("tickets")}
          >
            <FaTicketAlt />
            <span>Tickets</span>
            {myTickets.length > 0 && (
              <span className="tab-badge">{myTickets.length}</span>
            )}
          </button>
          <button
            ref={winsTabRef}
            className={`profile-tab ${activeTab === "wins" ? "active" : ""}`}
            onClick={() => handleTabChange("wins")}
          >
            <FaTrophy />
            <span>Wins</span>
            {auctionWins.length > 0 && (
              <span className="tab-badge">{auctionWins.length}</span>
            )}
          </button>
          {/* Perks tab — hidden until feature is ready
          <button
            ref={redeemedTabRef}
            className={`profile-tab ${activeTab === "redeemed" ? "active" : ""}`}
            onClick={() => handleTabChange("redeemed")}
          >
            <FaGift />
            <span>Perks</span>
            {redeemedCoupons.length > 0 && (
              <span className="tab-badge">{redeemedCoupons.length}</span>
            )}
          </button>
          */}
          <button
            ref={paymentTabRef}
            className={`profile-tab ${activeTab === "payment" ? "active" : ""}`}
            onClick={() => handleTabChange("payment")}
          >
            <FaCreditCard />
            <span>Payment</span>
            {paymentMethod?.has_payment_method && (
              <span className="tab-badge tab-badge--green">✓</span>
            )}
          </button>
        </div>

        {/* Tab Content */}
        <div className="profile-content" ref={contentRef}>
          {/* Auction Wins Tab */}
          {activeTab === "wins" && (
            <div className="profile-section">
              <h2 className="section-title">
                <FaTrophy />
                Auction Wins
              </h2>
              {winsError && (
                <div className="profile-wins-error">
                  <p>Could not load wins: {winsError}</p>
                  <button
                    className="profile-wins-retry"
                    onClick={async () => {
                      setWinsError(null);
                      try {
                        const data = await getAuctionWins(customerToken);
                        setAuctionWins(Array.isArray(data) ? data : []);
                      } catch (err) {
                        setWinsError(err.message || "Failed to load wins");
                      }
                    }}
                  >
                    Retry
                  </button>
                </div>
              )}
              {!winsError && auctionWins.length === 0 ? (
                <div className="empty-state-large">
                  <FaTrophy className="empty-icon" />
                  <h3>No wins yet</h3>
                  <p>Items you win in silent auctions will appear here</p>
                </div>
              ) : (
                <div className="profile-wins-list">
                  {auctionWins.map((win) => (
                    <div key={win.item_id} className="profile-win-card">
                      <div className="profile-win-img">
                        {win.image_url ? (
                          <img src={win.image_url} alt={win.title} />
                        ) : (
                          <div className="profile-win-img-placeholder">
                            <FaGavel />
                          </div>
                        )}
                      </div>
                      <div className="profile-win-body">
                        <div className="profile-win-title">{win.title}</div>
                        {win.artist_name && (
                          <div className="profile-win-artist">by {win.artist_name}</div>
                        )}
                        <div className="profile-win-event">{win.event_name}</div>
                        <div className="profile-win-bid">
                          ${Number(win.sold_price || 0).toFixed(2)}
                        </div>
                        <div className="profile-win-tags">
                          {win.charge_status === "charged" || win.is_paid ? (
                            <span className="win-tag win-tag--paid">
                              <FaCheckCircle /> Paid
                            </span>
                          ) : win.charge_status === "failed" ? (
                            <span className="win-tag win-tag--failed">
                              ⚠️ Payment failed — update your payment method
                            </span>
                          ) : win.charge_status === "no_payment_method" ? (
                            <span className="win-tag win-tag--failed">
                              ⚠️ No payment method on file
                            </span>
                          ) : (
                            <span className="win-tag win-tag--pending">Payment pending</span>
                          )}
                          {win.delivery_preference ? (
                            <span className="win-tag win-tag--delivery">
                              {win.delivery_preference === "delivery" ? "📦 Delivery" : "✅ Pickup"}
                            </span>
                          ) : (
                            <span className="win-tag win-tag--pending">No preference set</span>
                          )}
                        </div>

                        {/* Fulfillment form */}
                        {fulfillmentEditId === win.item_id ? (
                          <div className="win-fulfillment-form">
                            <div className="win-fulfillment-toggle">
                              <button
                                className={`win-pref-btn ${fulfillmentPref === "pickup" ? "active" : ""}`}
                                onClick={() => setFulfillmentPref("pickup")}
                                type="button"
                              >
                                ✅ Pickup
                              </button>
                              <button
                                className={`win-pref-btn ${fulfillmentPref === "delivery" ? "active" : ""}`}
                                onClick={() => setFulfillmentPref("delivery")}
                                type="button"
                              >
                                📦 Delivery <span className="win-pref-fee">(+$5)</span>
                              </button>
                            </div>
                            {fulfillmentPref === "delivery" && (
                              <input
                                className="win-fulfillment-input"
                                placeholder="Delivery address"
                                value={fulfillmentAddress}
                                onChange={(e) => setFulfillmentAddress(e.target.value)}
                              />
                            )}
                            <input
                              className="win-fulfillment-input"
                              placeholder="Preferred pickup/delivery time (optional)"
                              value={fulfillmentTime}
                              onChange={(e) => setFulfillmentTime(e.target.value)}
                            />
                            {fulfillmentErr && (
                              <p className="win-fulfillment-error">{fulfillmentErr}</p>
                            )}
                            <div className="win-fulfillment-actions">
                              <button
                                className="win-fulfillment-save"
                                onClick={() => handleFulfillmentSave(win)}
                                disabled={fulfillmentSaving}
                              >
                                {fulfillmentSaving ? "Saving…" : "Save"}
                              </button>
                              <button
                                className="win-fulfillment-cancel"
                                onClick={() => setFulfillmentEditId(null)}
                                disabled={fulfillmentSaving}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            className="win-fulfillment-edit-btn"
                            onClick={() => openFulfillmentEdit(win)}
                          >
                            {win.delivery_preference ? "Change pickup / delivery" : "Set pickup / delivery"}
                          </button>
                        )}
                      </div>
                      {win.event_slug && (
                        <button
                          className="profile-win-link"
                          onClick={() => navigate(`/events/${win.event_slug}/auction`)}
                        >
                          View →
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Payment Tab */}
          {activeTab === "payment" && (
            <div className="profile-section">
              <h2 className="section-title">
                <FaCreditCard />
                Payment Method
              </h2>
              <p className="payment-section-desc">
                Link a card, PayPal, or Venmo to bid in silent auctions. Winners are charged when the auction closes.
              </p>

              {paymentLoading ? (
                <div className="payment-modal-loading">
                  <span className="payment-modal-spinner" />
                  Loading…
                </div>
              ) : paymentMethod?.has_payment_method ? (
                <div className="payment-method-card payment-method-card--linked">
                  <div className="payment-method-icon">
                    {paymentMethod.type === "paypal" ? <FaPaypal /> : <FaCreditCard />}
                  </div>
                  <div className="payment-method-info">
                    {paymentMethod.type === "paypal" ? (
                      <>
                        <div className="payment-method-name">PayPal</div>
                        <div className="payment-method-detail">{paymentMethod.paypal_email || "Linked"}</div>
                      </>
                    // ) : paymentMethod.type === "venmo" ? (
                    //   <>
                    //     <div className="payment-method-name">Venmo</div>
                    //     <div className="payment-method-detail">@{paymentMethod.venmo_handle || "linked"}</div>
                    //   </>
                    ) : (
                      <>
                        <div className="payment-method-name">
                          {paymentMethod.brand
                            ? paymentMethod.brand.charAt(0).toUpperCase() + paymentMethod.brand.slice(1)
                            : "Card"}
                        </div>
                        <div className="payment-method-detail">····{paymentMethod.last4}</div>
                      </>
                    )}
                  </div>
                  <div className="payment-method-status">
                    <FaCheckCircle className="payment-status-icon" />
                    <span>Active</span>
                  </div>
                </div>
              ) : (
                <div className="payment-method-card payment-method-card--empty">
                  <FaCreditCard className="payment-empty-icon" />
                  <p>No payment method linked</p>
                </div>
              )}

              {paymentError && <div className="payment-error-msg">{paymentError}</div>}

              <div className="payment-actions">
                {(() => {
                  const anyBusy = linkingCard || linkingPaypal || removingPayment;
                  const hasMethod = paymentMethod?.has_payment_method;
                  return (
                    <>
                      {hasMethod && <div className="payment-replace-label"><FaExchangeAlt /> Replace with:</div>}
                      <div className="payment-action-btns">
                        <button
                          className="payment-btn payment-btn--card"
                          onClick={handleLinkCard}
                          disabled={anyBusy}
                        >
                          {linkingCard
                            ? <><span className="btn-spinner-sm" /> Redirecting…</>
                            : <><FaCreditCard /> {hasMethod ? "Replace Card" : "Link Card"}</>}
                        </button>
                        <button
                          className="payment-btn payment-btn--paypal"
                          onClick={handleLinkPaypal}
                          disabled={anyBusy}
                        >
                          {linkingPaypal
                            ? <><span className="btn-spinner-sm" /> Redirecting…</>
                            : <><FaPaypal /> {hasMethod ? "Replace PayPal" : "Link PayPal"}</>}
                        </button>
                        {/* Venmo link disabled
                        <button
                          className="payment-btn payment-btn--venmo"
                          onClick={() => { if (!anyBusy) setShowVenmoButton((v) => !v); }}
                          disabled={anyBusy}
                        >
                          {hasMethod ? "Replace Venmo" : "Link Venmo"}
                        </button>
                        */}
                      </div>

                      {/* Venmo link panel disabled
                      {showVenmoButton && (
                        <div style={{ marginTop: 12, maxWidth: 280 }}>
                          <VenmoLinkButton
                            createVaultSetupToken={handleVenmoSetupToken}
                            onApprove={handleVenmoApprove}
                            onError={(msg) => setPaymentError(msg || "Venmo setup failed.")}
                          />
                        </div>
                      )}
                      */}

                      {hasMethod && (
                        <button
                          className="payment-btn payment-btn--remove"
                          onClick={handleRemovePayment}
                          disabled={anyBusy}
                        >
                          {removingPayment
                            ? <><span className="btn-spinner-sm" /> Removing…</>
                            : <><FaTrash /> Remove Payment Method</>}
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Perks tab — hidden until feature is ready */}
          {/* {activeTab === "redeemed" && (
            <div className="profile-section">
              <h2 className="section-title">
                <FaGift />
                Perks Used
              </h2>
              {redeemedCoupons.length > 0 ? (
                <>
                  <ul className="redeemed-coupons-list">
                    {currentCoupons.map((coupon) => (
                      <li
                        key={coupon.coupon_issue_id}
                        className="redeemed-coupon-card"
                        onClick={() => handleGoToVenueCoupons(coupon.venue_id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleGoToVenueCoupons(coupon.venue_id)}
                        title={`View all coupons at ${coupon.venue_name || 'this venue'}`}
                      >
                      <div className="coupon-card-header">
                        <img
                          src={coupon.venue_logo_url || blockClubLogo}
                          alt="Venue logo"
                          className="coupon-logo"
                          onError={(e) => (e.currentTarget.src = blockClubLogo)}
                        />
                        <div className="coupon-status-badge redeemed">
                          <FaCheckCircle />
                          <span>Redeemed</span>
                        </div>
                      </div>
                      <div className="coupon-card-body">
                        <h3 className="coupon-title">{coupon.title}</h3>
                        {coupon.venue_name && (
                          <p className="coupon-venue-name">
                            @ {coupon.venue_name}
                          </p>
                        )}
                        {coupon.description && (
                          <p className="coupon-description">{coupon.description}</p>
                        )}
                        <div className="coupon-dates">
                          <div className="coupon-date-item">
                            <FaCalendarAlt />
                            <span>
                              Issued: {new Date(coupon.issued_at).toLocaleDateString("en-US", { timeZone: "America/Chicago" })}
                            </span>
                          </div>
                          {coupon.redeemed_at && (
                            <div className="coupon-date-item redeemed-date">
                              <FaCheckCircle />
                              <span>
                                Redeemed: {new Date(coupon.redeemed_at).toLocaleDateString("en-US", { timeZone: "America/Chicago" })}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>

                {totalPages > 1 && (
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <div className="pagination-pages">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                        <button
                          key={pageNum}
                          className={`pagination-page-btn ${currentPage === pageNum ? "active" : ""}`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      ))}
                    </div>
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
              ) : (
                <div className="empty-state-large">
                  <FaCheckCircle className="empty-icon" />
                  <h3>No Redeemed Coupons Yet</h3>
                  <p>Your redeemed coupons will appear here</p>
                </div>
              )}
            </div>
          )} */}

          {/* My Tickets Tab */}
          {activeTab === "tickets" && (
            <div className="profile-section">
              <h2 className="section-title">
                <FaTicketAlt />
                My Tickets
              </h2>
              {myTickets.length === 0 ? (
                <div className="empty-state-large">
                  <FaTicketAlt className="empty-icon" />
                  <h3>No Tickets Yet</h3>
                  <p>Tickets you purchase will appear here</p>
                  <button className="btn-browse" onClick={() => navigate("/")}>
                    <FaGift />
                    <span>Browse Events</span>
                  </button>
                </div>
              ) : (() => {
                const now = new Date();
                const upcoming = myTickets.filter(t => !t.start_at || new Date(t.start_at) >= now);
                const past = myTickets.filter(t => t.start_at && new Date(t.start_at) < now);

                const groupByEvent = (list) => {
                  const map = new Map();
                  list.forEach(t => {
                    if (!map.has(t.event_id)) map.set(t.event_id, []);
                    map.get(t.event_id).push(t);
                  });
                  return map;
                };

                const STATUS_ORDER = { valid: 0, used: 1, cancelled: 2, transferred: 3 };
                const sortTickets = (list) =>
                  [...list].sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9));

                const renderTicketGroup = (map, isPast) => [...map.entries()].map(([eventId, eventTickets]) => {
                  const sorted = sortTickets(eventTickets);
                  const isExpanded = expandedTicketEvents.has(eventId);
                  const hasMultiple = sorted.length > 1;
                  const eventSlug = sorted[0]?.event_slug;
                  return (
                    <div key={eventId} className="profile-event-group">
                      <div className="profile-event-group-header">
                        <span className="profile-event-group-title">
                          {sorted[0].event_title}{hasMultiple ? ` · ${sorted.length} tickets` : ""}
                        </span>
                        <div className="profile-event-group-actions">
                          {!isPast && eventSlug && (
                            <button
                              className="profile-buy-more-btn"
                              onClick={() => navigate(`/events/${eventSlug}/tickets`)}
                            >
                              + Buy more
                            </button>
                          )}
                          {hasMultiple && !isPast && (
                            <button
                              className="profile-expand-btn"
                              onClick={() => toggleTicketExpand(eventId)}
                            >
                              {isExpanded ? <><FaEyeSlash /> Hide QR</> : <><FaQrcode /> Show All QR</>}
                            </button>
                          )}
                        </div>
                      </div>
                      {sorted
                        .filter(ticket => !isExpanded || (!isPast && ticket.status === "valid"))
                        .map(ticket => {
                        const isInactive = isPast || ticket.status === "cancelled" || ticket.reservation_payment_status === "refunded";
                        return (
                        <div
                          key={ticket.ticket_id}
                          className={`profile-ticket-card${isPast ? " profile-ticket-card--past" : ""}${isInactive ? " profile-ticket-card--inactive" : ""}`}
                          onClick={isInactive ? undefined : () => navigate(`/tickets/${ticket.ticket_code}`)}
                          role={isInactive ? undefined : "button"}
                          tabIndex={isInactive ? -1 : 0}
                          onKeyDown={isInactive ? undefined : e => (e.key === 'Enter' || e.key === ' ') && navigate(`/tickets/${ticket.ticket_code}`)}
                        >
                          <div className="profile-ticket-left">
                            <div className="profile-ticket-event">{ticket.event_title}</div>
                            <div className="profile-ticket-tier">{ticket.tier_name}</div>
                            <div className="profile-ticket-meta">
                              {ticket.start_at && (
                                <span><FaCalendarAlt /> {new Date(ticket.start_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "America/Chicago" })}</span>
                              )}
                              {ticket.venue_name && (
                                <span><FaMapMarkerAlt /> {ticket.venue_name}</span>
                              )}
                            </div>
                            {isExpanded && !isPast && ticket.status === "valid" && (
                              <div className="profile-ticket-qr-inline">
                                {ticket.qr_image_url
                                  ? <img src={ticket.qr_image_url} alt="QR Code" className="profile-ticket-qr-img" />
                                  : <div className="profile-ticket-qr-placeholder"><FaQrcode /><span>Generating…</span></div>
                                }
                              </div>
                            )}
                          </div>
                          <div className="profile-ticket-right">
                            <span className={`profile-ticket-status profile-ticket-status--${isPast ? "expired" : ticket.status}`}>
                              {isPast ? "Expired" : ticket.status === "valid" ? "Valid" : ticket.status === "used" ? "Used" : "Cancelled"}
                            </span>
                            <FaTicketAlt className="profile-ticket-icon" />
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  );
                });

                return (
                  <div className="profile-tickets-list">
                    {upcoming.length > 0 && (
                      <div className="profile-tickets-group">
                        <h3 className="profile-tickets-group-title">
                          <FaCalendarCheck /> Upcoming
                        </h3>
                        {renderTicketGroup(groupByEvent(upcoming), false)}
                      </div>
                    )}
                    {past.length > 0 && (
                      <div className="profile-tickets-group">
                        <h3 className="profile-tickets-group-title profile-tickets-group-title--past">
                          <FaHistory /> Past Events
                        </h3>
                        {renderTicketGroup(groupByEvent(past), true)}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}


          {/* Preferences Tab — hidden until feature is ready
          {activeTab === "preferences" && (
            <div className="profile-section">
              <div className="section-header">
                <h2 className="section-title">
                  <FaCog />
                  My Preferences
                </h2>
                <button className="btn-edit" onClick={handleEditPreferences}>
                  <FaEdit />
                  <span>Edit Preferences</span>
                </button>
              </div>
              {preferences.length > 0 ? (
                <div className="preferences-grid">
                  {preferences.map((pref) => (
                    <div key={pref.tag_id} className="preference-card">
                      <div className="preference-icon">
                        <FaHeart />
                      </div>
                      <div className="preference-content">
                        <h3>{formatTagName(pref.name)}</h3>
                        {pref.description && (
                          <p className="preference-category">{pref.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state-large">
                  <FaCog className="empty-icon" />
                  <h3>No Preferences Set</h3>
                  <p>Set your preferences to get personalized coupon recommendations</p>
                  <button className="btn-browse" onClick={handleEditPreferences}>
                    <FaEdit />
                    <span>Set Preferences</span>
                  </button>
                </div>
              )}
            </div>
          )}
          */}
        </div>
      </div>

    </div>
  );
};

export default Profile;
